import { GetStaticPropsContext } from 'next/types';

import { AiMarketerPage } from '~/features/ai-marketer/components/ai-marketer-page';
import { AI_MARKETING_PAGE_PATH, START_LEARNING_PROGRAM_KEYS } from '~/features/ai-marketer/constants';
import { AiMarketerPageProps } from '~/features/ai-marketer/types';
import { PageCategory } from '~/features/analytics/constants';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';

export default function AiMarketingLandingPage({ page, startLearningPrograms }: AiMarketerPageProps) {
  return (
    <>
      <PageHead
        additionalPageAnalytics={{ label: 'AI Marketing' }}
        page={page}
        pageCategory={PageCategory.Subcategory}
      />
      <PageLayout withoutVerticalPadding>
        <AiMarketerPage startLearningPrograms={startLearningPrograms} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage(AI_MARKETING_PAGE_PATH);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const catalogItems = getCatalogItemsFromServer([...START_LEARNING_PROGRAM_KEYS]);
  const catalogItemsByKey: Record<string, CatalogItem> = {};

  for (const item of catalogItems) {
    catalogItemsByKey[item.key] = item;
  }

  const unresolvedKeys = START_LEARNING_PROGRAM_KEYS.filter((key) => !catalogItemsByKey[key]);

  if (unresolvedKeys.length > 0) {
    logger.fatal('Configured AI Marketer landing page programs are missing from the catalog', {
      file: 'src/pages/ai-marketing/index.tsx',
      function: 'getStaticProps',
      unresolvedKeys,
    });

    throw new Error(`Unresolved catalog keys for ${AI_MARKETING_PAGE_PATH}: ${unresolvedKeys.join(', ')}`);
  }

  const startLearningPrograms = START_LEARNING_PROGRAM_KEYS.map((key) => catalogItemsByKey[key]);

  return {
    props: {
      page,
      startLearningPrograms,
    } satisfies AiMarketerPageProps,
  };
}
